import { DEVICE_ID_HANDOFF_PARAM, parseHandoffDeviceId } from '../cookies/deviceIdHandoff';
import { ACTUAL_DOMAIN } from '../Domains';
import { matchesApex } from '../HostUtils';
import { trackingParamsQueryString } from '../urls/TrackingParams';
import { TABLERUN_APEX_HOST } from './hosts';

/**
 * Shared so shef-web and shef-api cannot drift on the storefront path.
 *
 * They already had: the router and the Shef Hub used `/tr/`, while the migration-announcement
 * email and the marketplace redirect built `/r/` — two customer-facing 404s. The prefix lived in
 * `shef-web/src/shared/Routes.ts`, which shef-api can't import, so nothing tied them together.
 */
export const TABLERUN_STOREFRONT_PATH_PREFIX = '/tr/';

export const tablerunStorefrontPath = (slug: string): string => `${TABLERUN_STOREFRONT_PATH_PREFIX}${slug}`;

export const TABLERUN_SUBSCRIPTIONS_PATH = '/account/subscriptions';

export const TABLERUN_ORDERS_PATH = '/account/orders/upcoming';

export const TABLERUN_SHEF_ORDERS_PATH = '/shef/orders/all';

const tablerunHostForShefHost = (shefHostname: string): string => {
  const [hostWithoutPort] = shefHostname.split(':');
  const environmentMatch = hostWithoutPort.match(/^([a-z0-9-]+)\.shef\.com$/);
  if (environmentMatch && environmentMatch[1] !== 'www') {
    return `${environmentMatch[1]}.${TABLERUN_APEX_HOST}`;
  }
  return TABLERUN_APEX_HOST;
};

/**
 * The TableRun origin for a shef.com origin, keeping its environment, for links built with no
 * request host to read (emails, background tasks).
 *
 * A non-shef.com origin, such as local dev, is returned unchanged: there TableRun is the same app
 * behind `?as=tablerun`, so pointing at production tablerun.com would send dev links to prod.
 */
export const tablerunOriginForShefOrigin = (shefOrigin: string): string => {
  const { hostname, origin } = new URL(shefOrigin);
  if (!matchesApex(hostname, ACTUAL_DOMAIN)) {
    return origin;
  }
  return `https://${tablerunHostForShefHost(hostname)}`;
};

/**
 * Builds the TableRun storefront a migrated shef's shef.com URL should land on, keeping the
 * caller's environment: `dev3.shef.com` hops to `dev3.tablerun.com`, never to production.
 *
 * @param shefHostname - the shef.com host the request arrived on. Express reports a port on
 *   `Host` where `window.location.hostname` does not, so both spellings are accepted.
 * @param search - the incoming query string, narrowed to tracking params before it is forwarded.
 * @param deviceId - the caller's own device id, handed across so the visitor is not a new device
 *   on the other apex. Ignored unless it is a well-formed uuid.
 */
export const tablerunStorefrontUrl = (
  shefHostname: string,
  slug: string,
  search = '',
  deviceId?: string | null
): string => {
  const base = `https://${tablerunHostForShefHost(shefHostname)}${tablerunStorefrontPath(slug)}`;
  const trackingQuery = trackingParamsQueryString(search);
  const handoffDeviceId = parseHandoffDeviceId(deviceId);

  if (!handoffDeviceId) {
    return `${base}${trackingQuery}`;
  }

  const separator = trackingQuery.length > 0 ? '&' : '?';
  return `${base}${trackingQuery}${separator}${DEVICE_ID_HANDOFF_PARAM}=${handoffDeviceId}`;
};
